import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Detail } from "./Detail";

const INK = "#0D1B2A";
const DYE = "#6B3FA0";
const MONO = "JetBrains Mono, monospace";

const STEPS = [
  { n: "01", title: "Volatiles leave the food", text: "Volatile basic compounds such as NH₃ rise from the meat into the sealed headspace and reach the semi-permeable membrane on the food side of the label." },
  { n: "02", title: "Only gas crosses the membrane", text: "The membrane passes small gas molecules. They dissolve into the moisture of the cellulose matrix in the indicator layer, where they shift the local pH — that shift is what the dye reads." },
  { n: "03", title: "Nothing comes back out", text: "The dye and the other larger molecules cannot pass the membrane: they are held in the indicator layer and stopped at the boundary. Nothing migrates into the food; only the colour leaves the label, through the window." },
];

const BANDS = [
  { y: 40, h: 26, fill: "#FFFFFF", label: "VIEWING WINDOW (PRINT LAYER)" },
  { y: 66, h: 34, fill: "#B99AD6", label: "INDICATOR LAYER (WITH DYE)" },
  { y: 100, h: 34, fill: "#EFEDE6", label: "NANO / MEMBRANE (SEMI-PERMEABLE)" },
  { y: 150, h: 70, fill: "#B5483F", label: "FOOD (MEAT)" },
];

const M_BANDS = [
  { y: 20, h: 30, fill: "#FFFFFF", label: "VIEWING WINDOW (PRINT)", ink: INK },
  { y: 50, h: 44, fill: "#B99AD6", label: "INDICATOR LAYER (DYE)", ink: INK },
  { y: 94, h: 44, fill: "#EFEDE6", label: "NANO / MEMBRANE", ink: INK },
  { y: 190, h: 60, fill: "#B5483F", label: "FOOD (MEAT)", ink: "#F2F3F5" },
];

const LANES = [110, 170, 230, 290];
const M_LANES = [230, 275, 320];

export const DiffusionDiagram = () => {
  const [sel, setSel] = useState(0);
  const [narrow, setNarrow] = useState(false);
  const s = STEPS[sel];

  useEffect(() => {
    setNarrow(window.matchMedia("(max-width: 767px)").matches);
  }, []);

  return (
    <div className="grid grid-cols-12 gap-x-6 gap-y-8" data-testid="diffusion-diagram">
      <div className="col-span-12 lg:col-span-7">
        {narrow ? (
          <svg viewBox="0 0 360 300" className="w-full h-auto">
            {M_BANDS.map((b) => (
              <g key={b.label}>
                <rect x="20" y={b.y} width="320" height={b.h} fill={b.fill} stroke={INK} />
                <text x="30" y={b.y + b.h / 2 + 4} fontFamily={MONO} fontSize="10" letterSpacing="0.5" fill={b.ink}>{b.label}</text>
              </g>
            ))}
            <rect x="200" y="20" width="120" height="30" fill="#F2F3F5" stroke={INK} />

            {sel < 2 &&
              M_LANES.map((x, i) => (
                <motion.circle
                  key={`m-gas-${x}`}
                  cx={x}
                  r="4"
                  fill={INK}
                  initial={{ cy: 188, opacity: 0 }}
                  animate={{ cy: sel === 0 ? [188, 142] : [188, 70], opacity: [0, 1, 1, 0] }}
                  transition={{ duration: sel === 0 ? 1.8 : 2.4, repeat: Infinity, delay: i * 0.4, ease: "easeInOut" }}
                  data-testid={`diffusion-gas-${i}`}
                />
              ))}

            {sel === 2 && (
              <g data-testid="diffusion-retained">
                {M_LANES.map((x, i) => (
                  <motion.circle
                    key={`m-dye-${x}`}
                    cx={x}
                    r="5"
                    fill={DYE}
                    stroke={INK}
                    strokeWidth="0.75"
                    initial={{ cy: 66 }}
                    animate={{ cy: [66, 86, 66] }}
                    transition={{ duration: 2.2, repeat: Infinity, delay: i * 0.25, ease: "easeInOut" }}
                  />
                ))}
                <line x1="20" y1="94" x2="340" y2="94" stroke={INK} strokeWidth="3" data-testid="diffusion-barrier" />
                {M_LANES.map((x) => (
                  <path key={`m-x-${x}`} d={`M${x - 6} 106 L ${x + 6} 120 M${x + 6} 106 L ${x - 6} 120`} stroke={INK} strokeOpacity="0.55" fill="none" />
                ))}
              </g>
            )}

            <text x="20" y="280" fontFamily={MONO} fontSize="10" letterSpacing="0.5" fill="#595959">
              {sel === 2 ? "DYE BLOCKED — NO MIGRATION" : "GAS ONLY, FOOD → LABEL ↑"}
            </text>
          </svg>
        ) : (
          <svg viewBox="0 0 640 240" className="w-full h-auto">
            {BANDS.map((b) => (
              <g key={b.label}>
                <rect x="40" y={b.y} width="320" height={b.h} fill={b.fill} stroke={INK} />
                <line x1="368" y1={b.y + b.h / 2} x2="392" y2={b.y + b.h / 2} stroke={INK} strokeOpacity="0.4" />
                <text x="398" y={b.y + b.h / 2 + 3} fontFamily={MONO} fontSize="9" letterSpacing="1" fill={INK}>{b.label}</text>
              </g>
            ))}
            <rect x="140" y="40" width="120" height="26" fill="#F2F3F5" stroke={INK} />

            {sel < 2 &&
              LANES.map((x, i) => (
                <motion.circle
                  key={`gas-${x}`}
                  cx={x}
                  r="3.5"
                  fill={INK}
                  initial={{ cy: 148, opacity: 0 }}
                  animate={{ cy: sel === 0 ? [148, 122] : [148, 83], opacity: [0, 1, 1, 0] }}
                  transition={{ duration: sel === 0 ? 1.8 : 2.4, repeat: Infinity, delay: i * 0.4, ease: "easeInOut" }}
                  data-testid={`diffusion-gas-${i}`}
                />
              ))}

            {sel === 2 && (
              <g data-testid="diffusion-retained">
                {LANES.map((x, i) => (
                  <motion.circle
                    key={`dye-${x}`}
                    cx={x}
                    r="4"
                    fill={DYE}
                    stroke={INK}
                    strokeWidth="0.75"
                    initial={{ cy: 80 }}
                    animate={{ cy: [80, 95, 80] }}
                    transition={{ duration: 2.2, repeat: Infinity, delay: i * 0.25, ease: "easeInOut" }}
                  />
                ))}
                <line x1="40" y1="100" x2="360" y2="100" stroke={INK} strokeWidth="3" data-testid="diffusion-barrier" />
                {LANES.map((x) => (
                  <path key={`x-${x}`} d={`M${x - 5} 108 L ${x + 5} 118 M${x + 5} 108 L ${x - 5} 118`} stroke={INK} strokeOpacity="0.55" fill="none" />
                ))}
                <text x="398" y="144" fontFamily={MONO} fontSize="9" letterSpacing="1" fill={INK}>DYE BLOCKED — NO MIGRATION</text>
              </g>
            )}

            <text x="40" y="236" fontFamily={MONO} fontSize="9" letterSpacing="1" fill="#595959">
              {sel === 2 ? "DYE RETAINED IN THE LABEL" : "GAS ONLY, FOOD → LABEL ↑"}
            </text>
          </svg>
        )}
        <div className="flex gap-2 mt-4" data-testid="diffusion-steps">
          {STEPS.map((st, i) => (
            <button
              key={st.n}
              type="button"
              onClick={() => setSel(i)}
              aria-pressed={sel === i}
              className="border px-4 py-3 min-h-[44px] eyebrow transition-colors duration-300"
              style={{ borderColor: sel === i ? INK : "rgba(13,27,42,0.25)", background: sel === i ? "#FFFFFF" : "transparent" }}
              data-testid={`diffusion-step-${st.n}`}
            >
              {st.n}
            </button>
          ))}
        </div>
      </div>
      <div className="col-span-12 lg:col-span-5">
        <Detail n={s.n} title={s.title} text={s.text} testId="diffusion-detail" />
      </div>
    </div>
  );
};
